import * as React from 'react';
import * as SiteSelect from '@/components/ui/select';
import * as OsSelect from '@/OS/components/ui/select';

type EmbeddedProp = {
  embedded?: boolean;
};

const EmbeddedSelectContext = React.createContext<boolean | undefined>(undefined);

const detectEmbedded = () => typeof document !== 'undefined' && Boolean(document.querySelector('.mapleos-root'));

const useEmbeddedSelect = (embedded?: boolean) => embedded ?? React.useContext(EmbeddedSelectContext) ?? detectEmbedded();

export function Select({ embedded, children, ...props }: React.ComponentProps<typeof SiteSelect.Select> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.Select : SiteSelect.Select) as typeof SiteSelect.Select;

  return (
    <EmbeddedSelectContext.Provider value={currentEmbedded}>
      <Component {...props}>{children}</Component>
    </EmbeddedSelectContext.Provider>
  );
}

export function SelectGroup({ embedded, ...props }: React.ComponentProps<typeof SiteSelect.SelectGroup> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectGroup : SiteSelect.SelectGroup) as typeof SiteSelect.SelectGroup;

  return <Component {...props} />;
}

export function SelectValue({ embedded, ...props }: React.ComponentProps<typeof SiteSelect.SelectValue> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectValue : SiteSelect.SelectValue) as typeof SiteSelect.SelectValue;

  return <Component {...props} />;
}

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectTrigger>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectTrigger> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectTrigger : SiteSelect.SelectTrigger) as typeof SiteSelect.SelectTrigger;

  return <Component ref={ref as never} {...props} />;
});
SelectTrigger.displayName = 'OSAwareSelectTrigger';

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectContent>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectContent> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectContent : SiteSelect.SelectContent) as typeof SiteSelect.SelectContent;

  return <Component ref={ref as never} {...props} />;
});
SelectContent.displayName = 'OSAwareSelectContent';

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectLabel>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectLabel> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectLabel : SiteSelect.SelectLabel) as typeof SiteSelect.SelectLabel;

  return <Component ref={ref as never} {...props} />;
});
SelectLabel.displayName = 'OSAwareSelectLabel';

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectItem>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectItem> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectItem : SiteSelect.SelectItem) as typeof SiteSelect.SelectItem;

  return <Component ref={ref as never} {...props} />;
});
SelectItem.displayName = 'OSAwareSelectItem';

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectSeparator>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectSeparator> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectSeparator : SiteSelect.SelectSeparator) as typeof SiteSelect.SelectSeparator;

  return <Component ref={ref as never} {...props} />;
});
SelectSeparator.displayName = 'OSAwareSelectSeparator';

export const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectScrollUpButton> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectScrollUpButton : SiteSelect.SelectScrollUpButton) as typeof SiteSelect.SelectScrollUpButton;

  return <Component ref={ref as never} {...props} />;
});
SelectScrollUpButton.displayName = 'OSAwareSelectScrollUpButton';

export const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SiteSelect.SelectScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SiteSelect.SelectScrollDownButton> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedSelect(embedded);
  const Component = (currentEmbedded ? OsSelect.SelectScrollDownButton : SiteSelect.SelectScrollDownButton) as typeof SiteSelect.SelectScrollDownButton;

  return <Component ref={ref as never} {...props} />;
});
SelectScrollDownButton.displayName = 'OSAwareSelectScrollDownButton';
